import { Engine } from 'json-rules-engine';

/**
 * Configuration for the business rule evaluator
 */
export interface BusinessRuleConfig {
    baseUrl: string;
    ruleName: string;
    cacheTTL?: number; // milliseconds, default: 1 hour
    requestTimeoutMs?: number; // milliseconds, default: 15 seconds
}

/**
 * Result of rule evaluation
 */
export interface ValidationResult {
    success: boolean;
    errors?: Record<string, string>;
}

/**
 * Business rule evaluator interface
 */
export interface BusinessRuleEvaluator {
    evaluate: (facts: any) => Promise<ValidationResult>;
    clearCache: () => void;
}

const VALIDATION_ENGINE_FAILURE_MESSAGE =
    "Validation service is temporarily unavailable. Please try again later.";

const DEFAULT_RULE_REQUEST_TIMEOUT_MS = 15000;

/**
 * Cached rule data
 */
interface RuleCache {
    rulesArray: any[];
    engine: Engine;
    fetchedAt: number;
}

/**
 * Creates a business rule evaluator that fetches rules from an API
 * and evaluates them client-side using json-rules-engine
 * 
 * @param config - Configuration including API base URL and rule name
 * @returns Business rule evaluator instance
 * 
 * @example
 * ```typescript
 * const evaluator = createBusinessRuleEvaluator({
 *   baseUrl: process.env.SVELTE_APP_ALADV_BASE_URL,
 *   ruleName: 'opportunity-validation'
 * });
 * 
 * const result = await evaluator.evaluate(formData);
 * if (!result.success) {
 *   console.error('Validation errors:', result.errors);
 * }
 * ```
 */
export function createBusinessRuleEvaluator(config: BusinessRuleConfig): BusinessRuleEvaluator {
    const {
        baseUrl,
        ruleName,
        cacheTTL = 3600000,
        requestTimeoutMs = DEFAULT_RULE_REQUEST_TIMEOUT_MS
    } = config; // Default 1 hour cache, 15 second request timeout
    let ruleCache: RuleCache | null = null;
    const AWS_EMAIL_REGEX =
        /^(?=.{0,80}$)[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

    const getLength = (value: any): number => {
        if (value === null || value === undefined) return 0;
        if (typeof value === "string" || Array.isArray(value)) return value.length;
        return String(value).length;
    };

    const isEmptyValue = (value: any): boolean =>
        value === null || value === undefined || value === "";

    const isValidTimestamp = (value: any): boolean => {
        if (typeof value !== "string" || value.trim() === "") return false;
        const parsed = Date.parse(value);
        return Number.isFinite(parsed);
    };

    /**
     * Registers custom operators with the engine
     */
    function registerCustomOperators(engine: Engine): void {
        // Regex pattern matching
        engine.addOperator('regex', (factValue: any, jsonValue: any) => {
            if (!factValue) return true; // null/empty values pass (handled by other rules)
            const regex = new RegExp(jsonValue);
            return regex.test(factValue);
        });

        // Length validation operators
        engine.addOperator('lengthEqual', (factValue: any, jsonValue: any) => {
            if (factValue === null || factValue === undefined) return true;
            return getLength(factValue) === jsonValue;
        });

        engine.addOperator('lengthGreaterThanInclusive', (factValue: any, jsonValue: any) => {
            if (factValue === null || factValue === undefined) return true;
            return getLength(factValue) >= jsonValue;
        });

        engine.addOperator('lengthLessThanInclusive', (factValue: any, jsonValue: any) => {
            if (factValue === null || factValue === undefined) return true;
            return getLength(factValue) <= jsonValue;
        });

        engine.addOperator('lengthBetween', (factValue: any, jsonValue: any) => {
            if (factValue === null || factValue === undefined) return true;
            const len = getLength(factValue);
            const [min, max] = jsonValue;
            return len >= min && len <= max;
        });

        engine.addOperator('doesNotContain', (factValue: any, jsonValue: any) => {
            if (Array.isArray(factValue)) return !factValue.includes(jsonValue);
            if (typeof factValue === "string") return !factValue.includes(String(jsonValue));
            return true;
        });

        engine.addOperator('arrayItemsFieldRequired', (factValue: any, jsonValue: any) => {
            if (!Array.isArray(factValue)) return false;
            if (!jsonValue || typeof jsonValue.field !== "string") return false;
            return factValue.every((item) => {
                if (!item || typeof item !== "object") return false;
                const fieldValue = item[jsonValue.field];
                return fieldValue !== null && fieldValue !== undefined && fieldValue !== "";
            });
        });

        engine.addOperator('arrayItemsFieldTimestamp', (factValue: any, jsonValue: any) => {
            if (!Array.isArray(factValue)) return false;
            if (!jsonValue || typeof jsonValue.field !== "string") return false;
            return factValue.every((item) => {
                if (!item || typeof item !== "object") return false;
                return isValidTimestamp(item[jsonValue.field]);
            });
        });

        engine.addOperator('arrayItemsFieldType', (factValue: any, jsonValue: any) => {
            if (!Array.isArray(factValue)) return false;
            if (!jsonValue || typeof jsonValue.field !== "string" || typeof jsonValue.type !== "string") {
                return false;
            }

            return factValue.every((item) => {
                if (!item || typeof item !== "object") return false;
                const fieldValue = item[jsonValue.field];
                if (jsonValue.type === "string") return typeof fieldValue === "string";
                if (jsonValue.type === "number") return typeof fieldValue === "number" && Number.isFinite(fieldValue);
                if (jsonValue.type === "boolean") return typeof fieldValue === "boolean";
                return false;
            });
        });

        engine.addOperator('arrayItemsFieldLengthLessThanInclusive', (factValue: any, jsonValue: any) => {
            if (!Array.isArray(factValue)) return false;
            if (!jsonValue || typeof jsonValue.field !== "string" || typeof jsonValue.max !== "number") {
                return false;
            }

            return factValue.every((item) => {
                if (!item || typeof item !== "object") return false;
                const fieldValue = item[jsonValue.field];
                if (isEmptyValue(fieldValue)) return jsonValue.allowEmpty === true;
                return getLength(fieldValue) <= jsonValue.max;
            });
        });

        engine.addOperator('arrayItemsFieldEmail', (factValue: any, jsonValue: any) => {
            if (!Array.isArray(factValue)) return false;
            if (!jsonValue || typeof jsonValue.field !== "string") return false;

            return factValue.every((item) => {
                if (!item || typeof item !== "object") return false;
                const fieldValue = item[jsonValue.field];
                if (fieldValue === null || fieldValue === undefined || fieldValue === "") return true;
                return typeof fieldValue === "string" && AWS_EMAIL_REGEX.test(fieldValue);
            });
        });

        engine.addOperator('arrayItemsFieldRegex', (factValue: any, jsonValue: any) => {
            if (!Array.isArray(factValue)) return false;
            if (!jsonValue || typeof jsonValue.field !== "string" || typeof jsonValue.regex !== "string") {
                return false;
            }

            const regex = new RegExp(jsonValue.regex);
            return factValue.every((item) => {
                if (!item || typeof item !== "object") return false;
                const fieldValue = item[jsonValue.field];
                if (fieldValue === null || fieldValue === undefined || fieldValue === "") return true;
                return typeof fieldValue === "string" && regex.test(fieldValue);
            });
        });

        // Date validation: ensures date is not in the past (YYYY-MM-DD)
        engine.addOperator('dateNotPast', (factValue: any, jsonValue: any) => {
            if (!jsonValue) return true;
            if (!factValue) return false;
            const parsed = new Date(`${factValue}T00:00:00`);
            if (Number.isNaN(parsed.getTime())) return false;
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            return parsed >= today;
        });
    }

    /**
     * Fetches rules from the API and creates an engine
     */
    async function fetchAndCacheRules(): Promise<void> {
        const ruleUrl = `${baseUrl}/aladv/business-rules/by-name/${ruleName}`;
        console.log('Fetching business rules from API:', ruleUrl);

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), requestTimeoutMs);

        let ruleData: any;
        try {
            const ruleRes = await fetch(
                ruleUrl,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    signal: controller.signal
                }
            );

            if (!ruleRes.ok) {
                throw new Error(`Business rule request failed: ${ruleRes.status}`);
            }

            ruleData = await ruleRes.json();
        } catch (err) {
            console.error(`Failed to fetch business rule "${ruleName}":`, err);
            throw new Error(VALIDATION_ENGINE_FAILURE_MESSAGE);
        } finally {
            window.clearTimeout(timeoutId);
        }
        console.log('Fetched rule:', ruleData);

        const rulesArray = ruleData.ruleJson;
        if (!Array.isArray(rulesArray)) {
            throw new Error('Invalid rule payload: ruleJson missing or not an array');
        }
        const engine = new Engine([], { allowUndefinedFacts: true });

        // Register custom operators
        registerCustomOperators(engine);

        // Add all rules to the engine
        rulesArray.forEach((rule: any) => {
            // Remove onFailure property as json-rules-engine doesn't support it
            const { onFailure, ...ruleWithoutOnFailure } = rule;
            engine.addRule(ruleWithoutOnFailure);
        });

        // Update cache
        ruleCache = {
            rulesArray: rulesArray,
            engine: engine,
            fetchedAt: Date.now()
        };

        console.log(`Rules cached for ${cacheTTL / 1000}s (${rulesArray.length} validation rules loaded)`);
    }

    // Convert undefined to null so "required" rules (notEqual null) catch missing fields.
    function normalizeFacts(value: any): any {
        if (value === undefined) return null;
        if (Array.isArray(value)) return value.map((item) => normalizeFacts(item));
        if (value && typeof value === "object") {
            const normalized: Record<string, unknown> = {};
            for (const [key, entryValue] of Object.entries(value)) {
                normalized[key] = normalizeFacts(entryValue);
            }
            return normalized;
        }
        return value;
    }

    // Collect all fact names referenced in a rule's conditions.
    function collectRuleFacts(condition: any, acc: Set<string>): void {
        if (!condition || typeof condition !== "object") return;
        if (typeof condition.fact === "string") acc.add(condition.fact);
        for (const key of ["all", "any", "not"]) {
            const sub = (condition as any)[key];
            if (Array.isArray(sub)) sub.forEach((c) => collectRuleFacts(c, acc));
            else if (sub) collectRuleFacts(sub, acc);
        }
    }

    // True if the rule needs a fact the form did not supply (validated server-side).
    function ruleReferencesUnavailableFact(rule: any, facts: any): boolean {
        const needed = new Set<string>();
        collectRuleFacts(rule?.conditions, needed);
        const available = facts ?? {};
        return Array.from(needed).some((fact) => !(fact in available));
    }

    /**
     * Evaluates a single rule against facts
     */
    async function evaluateSingleRule(rule: any, facts: any): Promise<boolean> {
        // Skip rules whose facts the form does not provide.
        if (ruleReferencesUnavailableFact(rule, facts)) return true;

        const tempEngine = new Engine([], { allowUndefinedFacts: true });
        registerCustomOperators(tempEngine);

        const { onFailure, ...ruleWithoutOnFailure } = rule;
        tempEngine.addRule(ruleWithoutOnFailure);

        const tempResult = await tempEngine.run(facts);
        return tempResult.events.length > 0;
    }

    function resolveDynamicFailureField(params: any, facts: any): string | undefined {
        if (
            !params ||
            typeof params.fieldTemplate !== "string" ||
            typeof params.arrayFact !== "string" ||
            typeof params.itemField !== "string" ||
            typeof params.itemOperator !== "string"
        ) {
            return undefined;
        }

        const items = facts?.[params.arrayFact];
        if (!Array.isArray(items)) return undefined;

        let failingIndex = -1;

        if (params.itemOperator === "email") {
            failingIndex = items.findIndex((item) => {
                if (!item || typeof item !== "object") return true;
                const value = item[params.itemField];
                if (value === null || value === undefined || value === "") return false;
                return typeof value !== "string" || !AWS_EMAIL_REGEX.test(value);
            });
        } else if (params.itemOperator === "regex" && typeof params.regex === "string") {
            const regex = new RegExp(params.regex);
            failingIndex = items.findIndex((item) => {
                if (!item || typeof item !== "object") return true;
                const value = item[params.itemField];
                if (value === null || value === undefined || value === "") return false;
                return typeof value !== "string" || !regex.test(value);
            });
        } else if (
            params.itemOperator === "lengthLessThanInclusive" &&
            typeof params.max === "number"
        ) {
            failingIndex = items.findIndex((item) => {
                if (!item || typeof item !== "object") return true;
                const value = item[params.itemField];
                if (value === null || value === undefined || value === "") return false;
                return getLength(value) > params.max;
            });
        }

        if (failingIndex < 0) return undefined;
        const field = params.fieldTemplate.replace("{index}", String(failingIndex));
        if (
            params.fieldTemplate === "contact-{index}" &&
            ["firstName", "lastName", "email", "businessTitle"].includes(params.itemField)
        ) {
            return `${field}-${params.itemField}`;
        }
        return field;
    }

    /**
     * Evaluates facts against business rules
     */
    async function evaluate(facts: any): Promise<ValidationResult> {
        console.log('Evaluating business rules...');
        let validationEngineFailed = false;

        const now = Date.now();

        try {
            // Check if we have a valid cached rule
            if (ruleCache && (now - ruleCache.fetchedAt) < cacheTTL) {
                console.log('Using cached business rule (age:', Math.round((now - ruleCache.fetchedAt) / 1000), 'seconds)');
            } else {
                // Cache is empty or expired, fetch the rule
                console.log(ruleCache ? 'Cache expired, refetching rule' : 'No cached rule, fetching from server');
                await fetchAndCacheRules();
            }
        } catch (err: any) {
            console.error('Failed to load business rules:', err);
            return {
                success: false,
                errors: { _general: VALIDATION_ENGINE_FAILURE_MESSAGE }
            };
        }

        if (!ruleCache) {
            return {
                success: false,
                errors: { _general: 'Failed to validate data. Please try again.' }
            };
        }

        const normalizedFacts = normalizeFacts(facts);

        // Run the cached engine with the facts (best-effort; validation uses per-rule evaluation)
        try {
            console.log('Running engine with facts:', normalizedFacts);
            const result = await ruleCache.engine.run(normalizedFacts);
            console.log('Engine result:', result);
        } catch (err) {
            console.error('Engine run failed, continuing with per-rule evaluation:', err);
            validationEngineFailed = true;
        }

        // Collect errors: rules that did NOT fire means validation failed
        const fieldErrors: Record<string, string> = {};

        // Check each rule individually to see if it would pass
        for (const rule of ruleCache.rulesArray) {
            try {
                const passed = await evaluateSingleRule(rule, normalizedFacts);

                // If this rule didn't fire an event, it failed
                if (!passed) {
                    if (rule.onFailure && rule.onFailure.params) {
                        const field = rule.onFailure.params.field;
                        const message = rule.onFailure.params.message;
                        const dynamicField = resolveDynamicFailureField(rule.onFailure.params, normalizedFacts);
                        const targetField = dynamicField || field;
                        if (targetField && message) {
                            fieldErrors[targetField] = message;
                        }
                    }
                }
            } catch (err) {
                console.error(`Error evaluating rule ${rule.name}:`, err);
                validationEngineFailed = true;
            }
        }

        if (validationEngineFailed) {
            return {
                success: false,
                errors: { _general: VALIDATION_ENGINE_FAILURE_MESSAGE }
            };
        }

        // Check if validation passed
        if (Object.keys(fieldErrors).length === 0) {
            return { success: true };
        }

        return { success: false, errors: fieldErrors };
    }

    /**
     * Clears the cached rules
     */
    function clearCache(): void {
        ruleCache = null;
        console.log('Business rule cache cleared');
    }

    return {
        evaluate,
        clearCache
    };
}
